// Keyboard + mouse capture (§8.1, §26). Presses are recorded as they happen and folded into the
// next sampled frame, so a press between two ticks is never lost. While a UI element has focus,
// arrows/Enter/Space/Tab/Escape belong to the menu (§9.1: arrow keys move focus, not the bean),
// and Q/E or PageUp/PageDown switch tabs when a `[data-tabs]` strip is on screen.
import type { BusEvents } from '../core/bus';
import { keyActionFor, type Bindings, type KeyAction } from './bindings';

export type NavAction = BusEvents['input:nav']['action'];

export interface KbmHooks {
  bindings(): Readonly<Bindings>;
  /** False while a menu has focus: no gameplay presses are recorded. */
  gameplayEnabled(): boolean;
  nav(action: NavAction): void;
  fullscreen(): void;
  mute(): void;
  /** Any keyboard or mouse use (device detection). */
  activity(): void;
  /**
   * Sees every key code / mouse button ('Mouse0'…) first; returning true consumes it (rebinding
   * capture, Practice item hotkeys).
   */
  raw?(code: string, repeat: boolean, mouse: boolean): boolean;
}

/** Actions that only ever produce gameplay presses or held state. */
const GAMEPLAY: ReadonlySet<KeyAction> = new Set<KeyAction>([
  'up', 'down', 'left', 'right', 'jump', 'shove', 'grab', 'item', 'honk', 'emote1', 'emote2', 'emote3', 'emote4',
]);
const MENU_KEYS: Readonly<Record<string, NavAction>> = { ArrowUp: 'up', ArrowDown: 'down', ArrowLeft: 'left', ArrowRight: 'right' };
const TAB_KEYS: Readonly<Record<string, NavAction>> = { KeyQ: 'tabL', KeyE: 'tabR', PageUp: 'tabL', PageDown: 'tabR' };
const NATIVE_WHEN_FOCUSED: ReadonlySet<string> = new Set(['Enter', 'NumpadEnter', 'Space', 'Tab']);
/** Mouse travel (CSS px) that counts as switching to KB+M (filters sensor jitter). */
const MOUSE_ACTIVITY_PX = 6;

function isTextField(t: EventTarget | null): boolean {
  return t instanceof HTMLInputElement || t instanceof HTMLTextAreaElement || t instanceof HTMLSelectElement;
}

/** True when a visible tab strip (`[data-tabs]`) is inside `root`. */
export function tabsVisible(root: ParentNode | null): boolean {
  if (!root) return false;
  for (const n of root.querySelectorAll<HTMLElement>('[data-tabs]')) if (n.offsetParent !== null) return true;
  return false;
}

export class KeyboardMouse {
  /** Mouse position in canvas CSS px and when it last moved (performance.now ms). */
  readonly mouse = { x: 0, y: 0, movedAt: -Infinity };
  private readonly held = new Set<string>();
  private readonly presses = new Map<KeyAction, number>();
  private readonly releases = new Map<KeyAction, number>();
  private canvas: HTMLCanvasElement | null = null;
  private overlay: HTMLElement | null = null;
  private readonly abort = new AbortController();
  private travel = 0;
  private readonly lastClient = { x: NaN, y: NaN };

  constructor(private readonly hooks: KbmHooks) {}

  attach(canvas: HTMLCanvasElement, overlay: HTMLElement): void {
    this.canvas = canvas;
    this.overlay = overlay;
    const signal = this.abort.signal;
    window.addEventListener('keydown', (e) => this.onKeyDown(e), { signal });
    window.addEventListener('keyup', (e) => this.onRelease(e.code), { signal });
    window.addEventListener('blur', () => this.releaseAll(), { signal });
    // Capture phase so rebinding sees clicks on any element (the settings panel included).
    window.addEventListener('pointerdown', (e) => {
      if (e.pointerType !== 'touch' && this.hooks.raw?.(`Mouse${e.button}`, false, true)) {
        e.preventDefault();
        e.stopPropagation();
      }
    }, { signal, capture: true });
    canvas.addEventListener('pointerdown', (e) => this.onPointerDown(e), { signal });
    window.addEventListener('pointerup', (e) => {
      if (e.pointerType !== 'touch') this.onRelease(`Mouse${e.button}`);
    }, { signal });
    window.addEventListener('pointermove', (e) => this.onPointerMove(e), { signal });
    canvas.addEventListener('contextmenu', (e) => e.preventDefault(), { signal });
  }

  detach(): void {
    this.abort.abort();
  }

  /** Presses of `a` since the last call. */
  takePresses(a: KeyAction): number {
    const n = this.presses.get(a) ?? 0;
    this.presses.set(a, 0);
    return n;
  }

  /** Releases of `a` since the last call (hold-to-carry synthesizes the throw from these). */
  takeReleases(a: KeyAction): number {
    const n = this.releases.get(a) ?? 0;
    this.releases.set(a, 0);
    return n;
  }

  isHeld(a: KeyAction): boolean {
    const [p, s] = this.hooks.bindings().keys[a];
    return (p !== null && this.held.has(p)) || (s !== null && this.held.has(s));
  }

  clearPresses(): void {
    this.presses.clear();
    this.releases.clear();
  }

  releaseAll(): void {
    for (const code of [...this.held]) this.onRelease(code);
  }

  /** True while an element of the UI overlay has keyboard focus (menus own the arrows then). */
  uiHasFocus(): boolean {
    const el = document.activeElement;
    return el !== null && el !== document.body && this.overlay !== null && this.overlay.contains(el);
  }

  get overlayRoot(): HTMLElement | null {
    return this.overlay;
  }

  private onKeyDown(e: KeyboardEvent): void {
    this.hooks.activity();
    if (this.hooks.raw?.(e.code, e.repeat, false)) {
      e.preventDefault();
      return;
    }
    if (isTextField(e.target)) {
      if (e.code === 'Escape') this.hooks.nav('back');
      return;
    }
    if (this.uiHasFocus()) {
      const nav = MENU_KEYS[e.code];
      if (nav) {
        // Held arrows auto-repeat through long lists (the OS key repeat), like the D-pad does.
        e.preventDefault();
        this.hooks.nav(nav);
        return;
      }
      if (e.code === 'Escape') {
        if (!e.repeat) this.hooks.nav('back');
        return;
      }
      const tab = TAB_KEYS[e.code];
      if (tab && tabsVisible(this.overlay)) {
        e.preventDefault();
        if (!e.repeat) this.hooks.nav(tab);
        return;
      }
      if (NATIVE_WHEN_FOCUSED.has(e.code)) return;
    }
    const action = keyActionFor(this.hooks.bindings(), e.code);
    if (!action) return;
    e.preventDefault();
    this.held.add(e.code);
    if (!e.repeat) this.press(action);
  }

  private onPointerDown(e: PointerEvent): void {
    if (e.pointerType === 'touch') return;
    this.hooks.activity();
    this.updateMouse(e);
    const code = `Mouse${e.button}`;
    const action = keyActionFor(this.hooks.bindings(), code);
    if (!action) return;
    e.preventDefault();
    this.held.add(code);
    this.press(action);
  }

  private onPointerMove(e: PointerEvent): void {
    if (e.pointerType === 'touch') return;
    this.updateMouse(e);
    this.mouse.movedAt = performance.now();
    if (Number.isFinite(this.lastClient.x)) this.travel += Math.hypot(e.clientX - this.lastClient.x, e.clientY - this.lastClient.y);
    this.lastClient.x = e.clientX;
    this.lastClient.y = e.clientY;
    if (this.travel >= MOUSE_ACTIVITY_PX) {
      this.travel = 0;
      this.hooks.activity();
    }
  }

  private updateMouse(e: PointerEvent): void {
    const r = this.canvas?.getBoundingClientRect();
    this.mouse.x = e.clientX - (r?.left ?? 0);
    this.mouse.y = e.clientY - (r?.top ?? 0);
  }

  private press(action: KeyAction): void {
    switch (action) {
      case 'menu': this.hooks.nav('pause'); return;
      case 'ready': this.hooks.nav('confirm'); return;
      case 'scoreboard': this.hooks.nav('scoreboard'); return;
      case 'fullscreen': this.hooks.fullscreen(); return;
      case 'mute': this.hooks.mute(); return;
      default:
        if (GAMEPLAY.has(action) && this.hooks.gameplayEnabled()) this.presses.set(action, (this.presses.get(action) ?? 0) + 1);
    }
  }

  private onRelease(code: string): void {
    if (!this.held.delete(code)) return;
    const action = keyActionFor(this.hooks.bindings(), code);
    if (action && !this.isHeld(action)) this.releases.set(action, (this.releases.get(action) ?? 0) + 1);
  }
}
